import { Button, ButtonLink, GridAlpha } from '@applyboard/crystal-ui';
import { useCurrentUser, useEventTracker } from '@applyboard/ui-utils';
import { useGetReferringRPId } from '../../common/hooks/useQueryParams';
import { appendQueryParameters } from '../../common/utils/appendQueryParameters';
import { VAS_HUB_TRACKING_SOURCE } from '../../pages/VasHub/constants';
import { RequestInformationDialog } from '../RequestInformation';
import { CardProps, default as ServiceCard, ServiceCardFooter } from '.';

export const StudentServiceCard = ({
  cardKey,
  badge = '',
  image,
  service,
  serviceLabel,
  vendor,
  isImageForAvatar = false,
  title,
  isTitleUnderImage = false,
  description,
  applyButtonCTAUrl,
  applyButtonName,
  vendorUrl,
  disabled = false,
  pricing,
  isSoldOut = false,
  isLoadingPrice = false,
  priceError = false,
}: CardProps) => {
  const { trackEvent } = useEventTracker();
  const [currentUser] = useCurrentUser();
  const rpId =
    useGetReferringRPId() ||
    (currentUser?.relationships as { agent?: { data: { id: string } } })?.agent?.data?.id ||
    '';

  const onApplyButtonClick = (rpId = '', card: string) => {
    trackEvent({
      event: `VAS - Vas Hub Page - Card Apply Button Clicked`,
      data: { rp_id: rpId, source: VAS_HUB_TRACKING_SOURCE, card: card },
    });
  };

  const NotAvailableButton = () => (
    <>
      <Button
        emphasis="transparent"
        intent="secondary"
        size="md"
        width="fill"
        disabled={disabled === true}
      >
        Not Available
      </Button>
    </>
  );

  const SoldOutButton = () => (
    <>
      <Button emphasis="transparent" intent="secondary" size="md" width="fill" disabled>
        Sold Out
      </Button>
    </>
  );

  const ContactSupportButton = () => (
    <>
      <Button emphasis="transparent" intent="secondary" size="md" width="fill" disabled>
        Contact Support
      </Button>
    </>
  );

  return (
    <>
      <ServiceCard
        cardKey={cardKey}
        serviceLabel={serviceLabel}
        badge={badge}
        image={image}
        isImageForAvatar={isImageForAvatar}
        title={title}
        isTitleUnderImage={isTitleUnderImage}
        description={description}
        shareLinkButtonName=""
        applyButtonCTAUrl={applyButtonCTAUrl}
        applyButtonName={applyButtonName}
        vendorUrl={vendorUrl}
        disabled={disabled}
        tooltip=""
        pricing={pricing}
        isSoldOut={isSoldOut}
        isLoadingPrice={isLoadingPrice}
        priceError={priceError}
      >
        <ServiceCardFooter>
          <GridAlpha columns="repeat(12, 1fr)" columnGap={4} rowGap={4}>
            <GridAlpha.Item
              columnStart={{
                xs: 1,
                sm: 1,
              }}
              columnEnd={{
                xs: 13,
                sm: 7,
              }}
              rowStart={{ xs: 1, sm: 1 }}
            >
              <RequestInformationDialog
                name={currentUser?.full_name ?? ''}
                email={currentUser?.email ?? ''}
                rpId={rpId}
                service={service ?? ''}
                vendor={vendor ?? ''}
              />
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 1,
                sm: 7,
              }}
              columnEnd={{
                xs: 13,
                sm: 13,
              }}
              rowStart={{ xs: 1, sm: 1 }}
            >
              {priceError && <ContactSupportButton />}
              {!priceError && isSoldOut && <SoldOutButton />}
              {!priceError && !isSoldOut && disabled && <NotAvailableButton />}
              {!priceError && !isSoldOut && !disabled && (
                <ButtonLink
                  intent="primary"
                  size="md"
                  emphasis="highlighted"
                  width="fill"
                  href={
                    // For logged in users: use cart link, for anonymous: use vendor URL
                    currentUser && applyButtonCTAUrl && applyButtonCTAUrl.trim() !== ''
                      ? appendQueryParameters(applyButtonCTAUrl, {
                          rp_id: rpId,
                          source: VAS_HUB_TRACKING_SOURCE,
                        })
                      : vendorUrl
                        ? appendQueryParameters(vendorUrl, {
                            rp_id: rpId,
                            source: VAS_HUB_TRACKING_SOURCE,
                          })
                        : ''
                  }
                  mode="override_user_choice_and_open_in_new_tab"
                  onClick={() => onApplyButtonClick(rpId, cardKey)}
                >
                  {isLoadingPrice ? `${applyButtonName} — Loading…` : applyButtonName}
                </ButtonLink>
              )}
            </GridAlpha.Item>
          </GridAlpha>
        </ServiceCardFooter>
      </ServiceCard>
    </>
  );
};

export default StudentServiceCard;
